import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { DoctorDataProvider } from './context/DoctorDataContext';
import { ToastProvider } from './components/common/ToastContainer';
import { useAppState, useAppActions, useNavigation, useCallRequest } from './hooks';
import TimezoneSelector from './components/doctor/TimezoneSelector';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Import components
import LandingScandinavian from './components/LandingScandinavian';
import CallRequestModal from './components/CallRequestModal';
import CallConfirmation from './components/CallConfirmation';
import DoctorLayout from './components/doctor/DoctorLayout';
import MarketplaceDashboard from './components/doctor/MarketplaceDashboard';
import DoctorCallRequests from './components/doctor/DoctorCallRequests';
import AdminApp from './components/admin/AdminApp';
import LoginForm from './components/auth/LoginForm';
import ConsultationPayment from './components/ConsultationPayment';
import PaymentSuccess from './components/PaymentSuccess';
import FAQ from './components/FAQ';
import TermsAndConditions from './components/TermsAndConditions';

// Error boundary component for better error handling
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen gradient-pastel flex items-center justify-center p-4">
          <div className="card max-w-md w-full text-center">
            <div className="text-6xl mb-4">😔</div>
            <h2 className="text-2xl font-bold text-luma-text mb-4">
              Oops! Something went wrong
            </h2>
            <p className="text-luma-text-light mb-6">
              Don't worry, your data is safe. Please go back to the home page and try again.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: undefined });
                  window.location.href = '/';
                }}
                className="flex-1 py-3 bg-[#38BDF8] text-white rounded-xl font-medium hover:bg-[#0EA5E9] transition-all"
              >
                Go Home
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 py-3 border border-[#334155]/20 text-[#334155] rounded-xl font-medium hover:bg-[#F0F9FF] transition-all"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Patient App Component
function PatientApp() {
  const state = useAppState();
  const actions = useAppActions();
  const navigation = useNavigation();
  const callRequest = useCallRequest();

  const {
    currentView,
    callConfirmation,
    isLoading,
    error
  } = state;

  console.log('🏠 PatientApp - Current view:', currentView);
  console.log('🏠 PatientApp - State:', { currentView, isLoading, error });

  // Global error display
  const renderError = () => {
    if (!error) return null;

    return (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-luma-error border border-luma-border text-luma-text px-6 py-4 rounded-3xl shadow-soft-lg max-w-md fade-in">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{error}</span>
            <button
              onClick={() => callRequest.startOver()}
              className="ml-4 text-luma-text hover:text-luma-primary transition-colors duration-300"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen gradient-pastel">
      {renderError()}
      
      {currentView === 'landing' && (
        <LandingScandinavian onBookNow={() => {
          console.log('🔘 Consult Now button clicked');
          try {
            navigation.handleBookNow();
          } catch (error) {
            console.error('❌ Error in handleBookNow:', error);
          }
        }} />
      )}
      
      {currentView === 'callRequest' && (
        <CallRequestModal
          isOpen={true}
          onClose={callRequest.cancelCallRequest}
          onSuccess={(requestId, amount) => {
            actions.setCallConfirmation({
              requestId: requestId,
              estimatedCallbackTime: 'within 24 hours',
              symptoms: [],
              amount: amount,
              preparationTips: [
                'Check your email for the Google Meet link',
                'Test your camera and microphone before the meeting',
                'Find a quiet, private space with good lighting',
                'Have a list of current medications ready',
                'Prepare any questions you want to ask',
                'Keep a pen and paper nearby for notes'
              ],
              confirmationMessage: 'Thank you! Your consultation request has been submitted successfully. A doctor will review and accept your request. You\'ll receive a Google Meet link via email once confirmed.'
            });
            console.log('✅ Set confirmation with requestId:', requestId);
            navigation.navigateTo('confirmation');
          }}
          isLoading={isLoading}
        />
      )}
      
      {currentView === 'confirmation' && callConfirmation && (
        <CallConfirmation
          confirmation={callConfirmation}
          onStartOver={callRequest.startOver}
        />
      )}
    </div>
  );
}

// Doctor App Component
function DoctorApp() {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<'dashboard' | 'requests' | 'analytics' | 'profile'>('dashboard');
  
  // Get doctor info from authenticated user
  // Use healthProfessionalId from backend login response
  const doctorId = user?.healthProfessionalId || user?.id || '';
  
  console.log('🔍 DOCTOR ID DEBUG:');
  console.log('  user?.email:', user?.email);
  console.log('  user?.id:', user?.id);
  console.log('  user?.healthProfessionalId:', user?.healthProfessionalId);
  console.log('  🎯 Using doctorId:', doctorId);
  
  // Validate doctor ID exists
  if (!doctorId) {
    console.error('❌ No doctor ID available! Login response must include healthProfessionalId');
  }
  
  const doctorName = user?.name || 'Doctor';

  // Debug logging for doctor ID
  useEffect(() => {
    if (user) {
      console.log('=== DOCTOR LOGIN INFO ===');
      console.log('Email:', user.email);
      console.log('Cognito ID (user.id):', user.id);
      console.log('Database ID (healthProfessionalId):', user.healthProfessionalId);
      console.log('Using doctorId for API calls:', doctorId);
      
      if (!user.healthProfessionalId) {
        console.warn('⚠️ WARNING: healthProfessionalId is missing from login response!');
        console.warn('⚠️ Backend must return healthProfessionalId in login response');
      }
    }
  }, [user, doctorId]);

  // Show loading if user data is not yet available
  if (!user) {
    return (
      <div className="min-h-screen gradient-pastel flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luma-primary mx-auto mb-4"></div>
          <p className="text-luma-text-light">Loading...</p>
        </div>
      </div>
    );
  }

  const handleBackToPatient = () => {
    window.location.href = '/';
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <MarketplaceDashboard 
            doctorId={doctorId}
          />
        );
      
      case 'requests':
        return (
          <DoctorCallRequests doctorId={doctorId} />
        );
      
      case 'analytics':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-luma-text">Analytics</h1>
              <p className="text-gray-600">Track your practice performance and revenue</p>
            </div>
            
            <div className="card">
              <h3 className="text-lg font-semibold text-luma-text mb-4">Coming Soon</h3>
              <p className="text-gray-600">
                Detailed analytics and reporting features will be available here.
              </p>
            </div>
          </div>
        );
      
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-luma-text">Profile</h1>
              <p className="text-gray-600">Manage your professional profile and settings</p>
            </div>
            
            {/* Timezone Settings */}
            <TimezoneSelector />
            
            <div className="card">
              <h3 className="text-lg font-semibold text-luma-text mb-4">Doctor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <p className="text-luma-text">Dr. {doctorName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-luma-text">doctor@shecare.com</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Practice Focus</label>
                  <p className="text-luma-text">General Practice</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                  <p className="text-luma-text">Licensed Practitioner</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <DoctorDataProvider doctorId={doctorId}>
      <DoctorLayout
        currentView={currentView}
        onViewChange={setCurrentView}
        doctorName={doctorName}
        onBackToPatient={handleBackToPatient}
      >
        {renderCurrentView()}
      </DoctorLayout>
    </DoctorDataProvider>
  );
}

// Admin App Component
function AdminAppWrapper() {
  const handleBackToApp = () => {
    window.location.href = '/';
  };

  return <AdminApp onBackToApp={handleBackToApp} />;
}

// Root App component with routing
function App() {
  // Load token debugging utilities in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import('./utils/tokenDebug');
    }
  }, []);

  // Check token expiry every minute
  useEffect(() => {
    const checkTokenExpiry = () => {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        return; // No token, user not logged in
      }

      try {
        const parts = authToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          
          // Check if token is expired
          if (payload.exp < currentTime) {
            console.log('🔒 Token expired - forcing logout');
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
          } else {
            // Log time until expiry (for debugging)
            const timeUntilExpiry = payload.exp - currentTime;
            console.log(`⏰ Token expires in ${Math.floor(timeUntilExpiry / 60)} minutes`);
          }
        }
      } catch (err) {
        console.error('Error checking token expiry:', err);
        // If we can't parse token, force logout
        console.log('🔒 Invalid token - forcing logout');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
      }
    };

    // Check immediately on mount
    checkTokenExpiry();

    // Check every minute
    const interval = setInterval(checkTokenExpiry, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <LanguageProvider>
          <AuthProvider>
            <Router>
              <Routes>
                {/* Admin routes - protected */}
                <Route path="/admin/*" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminAppWrapper />
                  </ProtectedRoute>
                } />
                
                {/* Doctor routes - protected */}
                <Route path="/doctor/*" element={
                  <ProtectedRoute requiredRole="doctor">
                    <DoctorApp />
                  </ProtectedRoute>
                } />
                
                {/* General login page - accessible to all */}
                <Route path="/login" element={<LoginForm />} />
                
                {/* FAQ and Terms - accessible to all */}
                <Route path="/faq" element={<FAQ />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                
                {/* Payment routes - accessible to all */}
                <Route path="/payment" element={<ConsultationPayment requestId="" />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                
                {/* Debug route to test routing */}
                <Route path="/test" element={
                  <div className="min-h-screen gradient-pastel flex items-center justify-center">
                    <div className="card text-center">
                      <h1 className="text-2xl font-bold text-luma-text mb-4">Test Route Works!</h1>
                      <p className="text-luma-text-light mb-4">Routing is working correctly.</p>
                      <a href="/" className="btn-primary">Go to Home</a>
                    </div>
                  </div>
                } />
                
                {/* Public patient routes */}
                <Route path="/*" element={
                  <AppProvider>
                    <PatientApp />
                  </AppProvider>
                } />
              </Routes>
            </Router>
          </AuthProvider>
        </LanguageProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
export default App;